import { useEffect, useState, lazy, Suspense, startTransition } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  PurchaseBundle,
  GetUserBundles,
  SwitchBundle,
} from "../PurchasesData.ts";
import { useProfile } from "../Context/ProfileContext";
import { useBudget } from "../Context/BudgetContext";
import {
  Container,
  Grid,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { toast } from "react-toastify";

// Lazy load toast container and toast
const ToastContainer = lazy(() =>
  import("react-toastify").then((module) => ({
    default: module.ToastContainer,
  }))
);

// Lazy load components that can be deferred
const ApartmentIcon = lazy(() => import("@mui/icons-material/Apartment"));
const BusinessCenterIcon = lazy(() =>
  import("@mui/icons-material/BusinessCenter")
);
const EngineeringIcon = lazy(() => import("@mui/icons-material/Engineering"));
const AppBundleWidget = lazy(() =>
  import("../sections/@dashboard/app").then((module) => ({
    default: module.AppBundleWidget,
  }))
);
const AiAdDialog = lazy(() => import("../components/AiAdDialog"));

export const BundlesPage = () => {
  const { profile, fetchProfile } = useProfile();
  const { budget, setBudget } = useBudget();
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = useState(false);
  const [ownedBundles, setOwnedBundles] = useState([]);

  const [reference, setReference] = useState("");
  const [amount, setAmount] = useState("");

  useEffect(() => {
    localStorage.setItem("isCardVisible", "true");
    localStorage.setItem("showAiAdDialog", "true");

    // Fetch user's owned bundles
    const fetchOwnedBundles = async () => {
      try {
        const response = await GetUserBundles();
        if (response.data) {
          setOwnedBundles(response.data);
        }
      } catch (error) {
        console.error("Error fetching owned bundles:", error);
      }
    };

    fetchOwnedBundles();
  }, []);

  // Helper function to get bundle info
  const getBundleInfo = (bundleType) => {
    const bundle = ownedBundles.find(
      (b) =>
        b.bundleType && b.bundleType.toLowerCase() === bundleType.toLowerCase()
    );
    return bundle;
  };

  // Handle bundle switching (no dialog, direct action)
  const handleSwitch = async (bundleType) => {
    try {
      const switchResponse = await SwitchBundle({
        UserId: profile.id,
        BundleType: bundleType,
      });

      if (switchResponse.data) {
        toast.success("Bundle switched successfully!", {
          position: "top-center",
          autoClose: 1000,
        });
        fetchProfile();

        // Refresh owned bundles
        const response = await GetUserBundles();
        if (response.data) {
          setOwnedBundles(response.data);
        }
      }
    } catch (error) {
      toast.error("Failed to switch bundle", {
        position: "top-center",
        autoClose: 1000,
      });
      if (error.redirectToLogin) {
        navigate("/Login");
      }
    }
  };

  const handlePurchase = async (ref, amount) => {
    try {
      await startTransition(async () => {
        // This should only handle purchase or renewal now (switch is handled separately)
        // Check budget
        if (budget < amount) {
          toast.error("Your budget is not enough to make this purchase!", {
            position: "top-center",
            autoClose: 1000,
          });
          return;
        }

        const response = await PurchaseBundle({
          UserId: profile.id,
          Reference: ref,
          Date: new Date(),
          Amount: amount,
          Country: profile.profile.country,
        });

        if (response.data) {
          setBudget(response.data.newAmount);

          const message =
            response.data.action === "renew"
              ? "Bundle renewed successfully!"
              : "Bundle purchased successfully!";

          toast.success(message, {
            position: "top-center",
            autoClose: 1000,
          });

          if (ref === "Student") {
            localStorage.setItem("showAiAdDialog", "true");
          }

          // Refresh owned bundles
          const bundlesResponse = await GetUserBundles();
          if (bundlesResponse.data) {
            setOwnedBundles(bundlesResponse.data);
          }
        }

        setOpenDialog(false);
        fetchProfile();
      });
    } catch (error) {
      if (error.redirectToLogin) {
        navigate("/Login");
      }
    }
  };

  return (
    <>
      <Container>
        <Helmet>
          <title> Contactie | Bundles </title>
        </Helmet>
        <Typography variant="h5" sx={{ mb: 4 }}>
          Bundles
        </Typography>
        <Grid container spacing={4}>
          {/* <Grid item xs={12} sm={6} md={4}>
                        <Suspense fallback={<CircularProgress />}>
                            <AppBundleWidget
                                title="Free"
                                amount={0}
                                reference={"Free"}
                                icon={<EmojiPeopleIcon fontSize="large" />}
                                data={[
                                    "include Social Links (Up to 5)",
                                    "include Custom Links (1)",
                                    "Media Pictures (Up to 5)",
                                    "YouTube Integration (Links to videos)",
                                    "Enhanced Biography (About)",
                                    "Analytic Tracking (View performance)",
                                    "Rating System (coupons)",
                                ]}
                                isFreeBundle={true}
                            />
                        </Suspense>
                    </Grid> */}

          <Grid item xs={12} sm={6} md={4}>
            <Suspense fallback={<CircularProgress />}>
              <AppBundleWidget
                title="Student"
                amount={25}
                reference={"Student"}
                icon={<BusinessCenterIcon fontSize="large" />}
                data={[
                  "include Social Links (Unlimited)",
                  "include Custom Links (Up to 1)",
                  "include Media Pictures (Up to 5)",
                  "include YouTube Integration (Links to videos)",
                  "include Enhanced Biography (About)",
                  "Analytic Tracking (View performance)",
                  "Rating Rating System (coupons)",
                ]}
                setOpenDialog={setOpenDialog}
                setReference={setReference}
                setAmount={setAmount}
                currentUserCategory={profile?.category}
                ownedBundles={ownedBundles}
                onSwitch={handleSwitch}
              />
            </Suspense>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Suspense fallback={<CircularProgress />}>
              <AppBundleWidget
                title="Freelance"
                amount={35}
                reference={"Freelance"}
                icon={<EngineeringIcon fontSize="large" />}
                data={[
                  "include Social Links (Unlimited)",
                  "include Custom Links (Up to 1)",
                  "include Media Pictures (Up to 5)",
                  "include YouTube Integration (Links to videos)",
                  "include Enhanced Biography (About)",
                  "include Rating System (coupons)",
                  "Analytic Tracking (View performance)",
                ]}
                setOpenDialog={setOpenDialog}
                setReference={setReference}
                setAmount={setAmount}
                currentUserCategory={profile?.category}
                ownedBundles={ownedBundles}
                onSwitch={handleSwitch}
              />
            </Suspense>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Suspense fallback={<CircularProgress />}>
              <AppBundleWidget
                title="Enterprise"
                amount={50}
                reference={"Enterprise"}
                article={"Recommended"}
                icon={<ApartmentIcon fontSize="large" />}
                data={[
                  "include Social Links (Unlimited)",
                  "include Custom Links (Up to 1)",
                  "include Media Pictures (Up to 5)",
                  "include YouTube Integration (Links to videos)",
                  "include Enhanced Biography (About)",
                  "include Analytic Tracking (View performance)",
                  "include Rating System (coupons)",
                ]}
                setOpenDialog={setOpenDialog}
                setReference={setReference}
                setAmount={setAmount}
                currentUserCategory={profile?.category}
                ownedBundles={ownedBundles}
                onSwitch={handleSwitch}
              />
            </Suspense>
          </Grid>
        </Grid>
        <Suspense fallback={<CircularProgress />}>
          <ToastContainer />
        </Suspense>
      </Container>
      <Suspense fallback={<CircularProgress />}>
        <AiAdDialog />
      </Suspense>
      <Dialog
        open={openDialog}
        onClose={() => {
          setOpenDialog(false);
        }}
      >
        <DialogTitle color="primary">
          {(() => {
            const bundleInfo = getBundleInfo(reference);
            if (bundleInfo && bundleInfo.isExpired) {
              return "Renew Bundle";
            } else {
              return "Purchase Bundle";
            }
          })()}
        </DialogTitle>

        <DialogContent>
          <Typography
            sx={{
              padding: "20px",
              color: "text.secondary",
            }}
          >
            {(() => {
              const bundleInfo = getBundleInfo(reference);
              if (bundleInfo && bundleInfo.isExpired) {
                return `Your ${reference} bundle has expired. Renew it for ${amount} dt?`;
              } else {
                return `Are you sure you want to purchase ${reference} bundle for ${amount} dt?`;
              }
            })()}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenDialog(false);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              handlePurchase(reference, amount);
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
